import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Heading,
  Text,
} from '@chakra-ui/react';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { articleJobWorkspaceStyles as styles } from '@texas/resources/theme/styles/entityWorkspace';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleJobAction } from '../ArticleJobAction';
import { useCopyArticle } from './useCopyArticle';

export function CopyArticleSummary() {
  const { t } = useTranslation();
  const {
    source,
    destinationCustomerId,
    destinationCustomerOption,
    selectedConceptOption,
    copyTargets,
    includedVariants,
    includedArticleDataCount,
    canSubmit,
    clashingItemCodeCount,
    firstArticleClashes,
    ignoredCount,
    ignoreClashingItemCodes,
    itemCodeLengthInvalid,
    itemCodeMinLength,
    itemCodeMaxLength,
    unavailable,
    copyError,
    copying,
    setValue,
    submit,
  } = useCopyArticle();
  if (!source) return null;

  return (
    <Box sx={styles.summary} position="static" maxH="none">
      <Box sx={styles.summaryContent} tabIndex={0}>
        <Heading as="h2" sx={styles.summaryTitle}>
          {t('article.create.summary')}
        </Heading>
        <Box sx={styles.summaryCustomer}>
          <Text sx={styles.summarySecondary}>
            {t('article.copy.destinationTitle')}
          </Text>
          {destinationCustomerOption && destinationCustomerId ? (
            <AngularNavLink
              to="customer"
              navobj={{ customerId: destinationCustomerId }}
              sx={styles.summaryCustomerLink}
            >
              {destinationCustomerOption.label}
            </AngularNavLink>
          ) : (
            <Text sx={styles.summarySecondary}>—</Text>
          )}
          <Text sx={styles.summarySecondary}>
            {t('article.copy.concept')} ·{' '}
            {selectedConceptOption?.label ?? t('article.copy.noConcept')}
          </Text>
        </Box>
        <Box sx={styles.summaryGroup}>
          <Box sx={styles.summaryCountRow}>
            <Text sx={styles.summaryCount}>{copyTargets.length}</Text>
            <Text sx={styles.summaryCountLabel}>
              {t('article.create.articlesToCreate', {
                count: copyTargets.length,
              })}
            </Text>
          </Box>
          <Text sx={styles.summarySecondary}>
            {t('article.copy.newArticleCount', { count: copyTargets.length })}
          </Text>
        </Box>
        <Box as="dl" sx={styles.summaryDetails}>
          <Text as="dt">{t('article.copy.sourceLabel')}</Text>
          <Text as="dd">{source.itemCode}</Text>
          <Text as="dt">{t('article.copy.variants')}</Text>
          <Text as="dd">
            {t('article.copy.includedCount', {
              count: includedVariants.length,
            })}
          </Text>
          <Text as="dt">{t('article.copy.articleData')}</Text>
          <Text as="dd">
            {t('article.copy.includedCount', {
              count: includedArticleDataCount,
            })}
          </Text>
        </Box>
        <Box sx={styles.summaryGroup}>
          <Heading as="h3" sx={styles.summaryCountLabel}>
            {t('article.copy.includedData')}
          </Heading>
          <Box as="dl" sx={styles.summaryIncludedDetails}>
            {includedVariants.map((variant) => (
              <Fragment key={variant.variantId}>
                <Text as="dt">
                  {
                    source.variants.find(
                      (item) => item.id === variant.variantId,
                    )?.name
                  }
                </Text>
                <Text as="dd">{t('article.copy.included')}</Text>
              </Fragment>
            ))}
          </Box>
        </Box>
        <CopyArticleStatusDescription
          canSubmit={canSubmit}
          clashingItemCodeCount={clashingItemCodeCount}
          ignoreClashingItemCodes={ignoreClashingItemCodes}
        />
      </Box>
      <Box sx={styles.summaryActions}>
        {copyError && <ErrorDetails error={copyError} />}
        {(itemCodeLengthInvalid ||
          (!firstArticleClashes &&
            unavailable &&
            !ignoreClashingItemCodes)) && (
          <Alert status="error" sx={styles.summaryAlert}>
            <AlertIcon />
            <AlertDescription>
              {itemCodeLengthInvalid
                ? t('article.copy.itemCodeLengthRequirement', {
                    minimum: itemCodeMinLength,
                    maximum: itemCodeMaxLength,
                  })
                : t('article.copy.unavailableHelp')}
            </AlertDescription>
          </Alert>
        )}
        <ArticleJobAction
          variant="sidebar"
          title={
            canSubmit
              ? t('article.copy.readyToCreate')
              : t('article.copy.reviewRequired')
          }
          description={t('article.copy.actionDescription')}
          buttonLabel={t('article.copy.copyButton', {
            count: copyTargets.length,
          })}
          showIgnoreClashes={!firstArticleClashes}
          ignoreClashes={ignoreClashingItemCodes}
          ignoredCount={ignoredCount}
          onIgnoreChange={(value) => setValue('ignoreClashingItemCodes', value)}
          canSubmit={canSubmit}
          loading={copying}
          onSubmit={submit}
        />
      </Box>
    </Box>
  );
}

function CopyArticleStatusDescription({
  canSubmit,
  clashingItemCodeCount,
  ignoreClashingItemCodes,
}: {
  canSubmit: boolean;
  clashingItemCodeCount: number;
  ignoreClashingItemCodes: boolean;
}) {
  const { t } = useTranslation();

  if (!canSubmit)
    return (
      <Text sx={styles.summarySecondary}>
        {t('article.copy.reviewDescription')}
      </Text>
    );

  if (clashingItemCodeCount > 0 && ignoreClashingItemCodes)
    return (
      <Text sx={styles.summarySecondary}>
        {t('article.copy.ignoredClashingItemCodes', {
          count: clashingItemCodeCount,
        })}
      </Text>
    );

  return (
    <Text sx={styles.summarySecondary}>
      {t('article.copy.readyDescription')}
    </Text>
  );
}
